import { useEffect, useState } from "react";
import { useTranslation } from "@ncp/i18n";
import { cn } from "@ncp/ui";

const SLOW_MS = 8_000;

function Bar({ className }: { className?: string }) {
  return <span aria-hidden className={cn("os-shimmer block rounded-full", className)} />;
}

/** process-kit's `.pk-inner`: the inner glass panel the hero and the tabs row sit on. */
const inner = "rounded-[20px] border border-[rgb(255_255_255/0.5)] bg-[rgb(255_255_255/0.72)] shadow-[0_1px_3px_rgb(80_55_10/0.05)]";

/**
 * What an app window shows until its page is ready: process-kit's own first view, block for block (`AppSkeleton` in
 * `spaces/economic-studies/libs/process-kit/states.tsx`, inside the kit's column in `root.tsx`): the hero 208px, the tabs row 44px and six
 * cards 188px. The page's breakpoints are those of its frame, not of the browser window (the dock and the window's width narrow it), so
 * the column answers to its own width through container queries at the same 640 / 768 / 1024 / 1280px steps. The swap to the page is
 * then seamless. After 8 seconds one line says the first open takes longer; it floats over the cards, so nothing moves.
 *
 * PageHost wraps the placeholder in a polite live region: the hidden "Opening…" line and the slow line are what it announces.
 */
export function AppSkeleton() {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_MS);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div aria-busy="true" className="@container relative h-full overflow-hidden" data-app-skeleton>
      <span className="sr-only">{t("assistant:os.window.loading")}</span>
      <div className="mx-auto w-full max-w-[1216px] space-y-5 px-4 pt-4 pb-10 @min-[768px]:space-y-6 @min-[768px]:px-6 @min-[768px]:pt-5 @min-[1024px]:px-8">
        <div className={cn(inner, "relative h-[208px] overflow-hidden px-4 py-5 @min-[768px]:px-6")}>
          <div className="flex gap-5">
            <Bar className="size-14 shrink-0 rounded-[14px] @min-[640px]:size-[4.5rem]" />
            <div className="min-w-0 flex-1 space-y-3 pt-1">
              <Bar className="h-2.5 w-40" />
              <Bar className="h-7 w-2/3 max-w-md" />
              <Bar className="h-3 w-full max-w-xl" />
              <Bar className="h-5 w-36" />
            </div>
          </div>
          <Bar className="absolute inset-x-4 bottom-4 h-12 rounded-[14px] @min-[768px]:inset-x-6" />
        </div>
        <div className={cn(inner, "flex h-[44px] items-center gap-2 p-1.5")}>
          {["w-24", "w-20", "w-[5.5rem]", "w-[4.5rem]"].map((w) => <Bar key={w} className={cn("h-8", w)} />)}
        </div>
        <div className="grid grid-cols-1 gap-3 @min-[640px]:grid-cols-2 @min-[1280px]:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-[188px] rounded-[16px] border border-white/60 bg-white/55 p-4 @min-[640px]:p-5">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1 space-y-2.5">
                  <Bar className="h-2.5 w-20" />
                  <Bar className="h-4 w-4/5" />
                  <Bar className="h-3 w-3/5" />
                </div>
                <Bar className="size-14 shrink-0" />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2">
                <Bar className="h-9 rounded-[12px]" />
                <Bar className="h-9 rounded-[12px]" />
                <Bar className="h-9 rounded-[12px]" />
              </div>
              <Bar className="mt-4 h-3 w-1/3" />
            </div>
          ))}
        </div>
      </div>
      {slow ? (
        <p className="os-fade-in absolute inset-x-0 bottom-6 mx-auto w-fit max-w-[calc(100%-2rem)] rounded-full bg-white/90 px-4 py-2 text-center text-[13px] text-sand-700 shadow-[var(--shadow-pill)]">
          {t("assistant:os.window.slow")}
        </p>
      ) : null}
    </div>
  );
}
